/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	Fragment,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
	ApButtonIcon,
	ApButtonLink,
	ApCheckbox,
	ApLoaderDefault,
	ApLoaderText,
	ApIcon,
	colors,
} from '@alixpartners/ui-components';
import debounce from 'lodash/debounce';
import { StyledModal, Wrapper } from './SearchDialog.styles';
import { MoveToBinViewModal } from 'Details/MoveToBinModal/MoveToBinModal';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { FilterChip, FilterChipGroup } from 'components/FilterChip';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { convertMeasuresToWaterfallFilter } from 'utils/detailsChartDataConverter';
import { convertToArray, isCustomCategory, UNITS_MAP } from 'Details/utils';
import {
	getSearchResults,
	getTransactionsBinDimensions,
	moveMultipleToBin,
} from 'utils/apiRequests';
import { getPropsFromQueryStringConsumer } from 'Pmi/utils';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import { DimensionDropdown } from './DimensionDropdown';
import {
	getCols,
	getColumnValue,
	getTags,
	SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY,
	getRequestParams,
	showAnalysisExplorePage,
	showPMIExplorePage,
} from './utils';
import {
	Container,
	FiltersContainer,
	FiltersLabel,
	ChipsContainer,
	NavigateLink,
	Selection,
	QuickSearchContainer,
	QuickSearchLabel,
	QuickSearchOptions,
	TableHeader,
	DialogSearchWrapper,
	StyledSearch,
	TableHeaderCol,
	TableRows,
	TableRow,
	TableCell,
	TableEllipsisCell,
	Pagination,
	PaginationText,
	DeleteButton,
} from './SearchDialog.styles';
import { isEqual, uniqBy } from 'lodash';

const getTop3Selected = (prevSelectedItems = [], item) => {
	const newSelected = uniqBy(
		[
			...prevSelectedItems,
			{
				...item,
				date: new Date(),
			},
		],
		'value'
	);
	if (newSelected.length <= 3) {
		return newSelected;
	}
	return newSelected.splice(0, 3);
};

export const SearchDialog = props => {
	const { isPmi } = props;
	const history = useHistory();
	const queryStringState = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const { permissions } = useContext(PermissionsContext);
	const { currency: currencyList } = options;

	const { currency } = queryStringState;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const convertedPropsFromQueryString = getPropsFromQueryStringConsumer(
		queryStringState
	);
	const [itemsOnPage, addItemsOnPage] = useState(25);
	const [dimensions, setDimensions] = useState([]);
	const [selectedDimension, setSelectedDimension] = useState(null);
	const [prevSelectedDimensions, setPrevSelectedDimensions] = useState([]);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isMoveToBinOpen, setIsMoveToBinOpen] = useState(false);
	const [search, setSearch] = React.useState('');
	const [transactions, setTransactions] = React.useState([]);
	const [selectedTransactions, setSelectedTransactions] = useState([]);
	const [isAllSelectedTransactions, setIsAllSelectedTranscations] = useState(
		false
	);
	const [sorting, setSorting] = useState({
		// Make default sorting by spend
		columnIndex: 3,
		direction: 'DESC',
	});
	const [quickFilter, setQuickFilter] = React.useState(undefined);
	const onSearchChange = query => {
		setQuickFilter(0);
		setSearch(query);
	};
	const onSearchChangeDebounced = debounce(onSearchChange, 200);

	const onSearchClear = () => {
		setSearch('');
	};

	const params = getRequestParams({
		...convertedPropsFromQueryString,
		...props,
		selectedDimension,
		currency,
		itemsOnPage,
		quickFilter,
		search,
		sorting,
	});

	const paramsRef = useRef(params);

	if (!isEqual(paramsRef.current, params)) {
		paramsRef.current = params;
	}

	useEffect(
		() => {
			if (permissions && permissions.includes(PERMISSIONS.TransactionsWrite)) {
				getTransactionsBinDimensions().then(result => {
					setDimensions(result);
				});
			}
			const localStorageDimensions = getLocalStorageItem(
				SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY
			);
			if (localStorageDimensions) {
				setPrevSelectedDimensions(localStorageDimensions);
			}
		},
		[permissions]
	);
	useEffect(
		() => {
			if (paramsRef.current) {
				getSearchResults(paramsRef.current).then(result => {
					setTransactions(result);
				});
			}
		},
		[paramsRef.current]
	);

	useEffect(
		() => {
			if (!selectedDimension && dimensions.length) {
				setSelectedDimension({
					...dimensions[0],
					label: dimensions[0].displayName.replaceAll('_', ' '),
					value: dimensions[0].displayName,
				});
			}
		},
		[dimensions, selectedDimension]
	);

	const moveToBin = () => {
		const params = getRequestParams({
			...convertedPropsFromQueryString,
			...props,
			selectedDimension,
			currency,
			itemsOnPage,
			quickFilter,
			search,
			sorting,
		});

		if (params) {
			const selectedItems = selectedTransactions
				.map(transactionValue => {
					const transaction = transactions.body.find(
						rowItem => rowItem.value === transactionValue
					);
					if (transaction) {
						return {
							dimensionName: selectedDimension.dimensionName,
							value: transaction.value,
							dimensionValues: transaction.dimensionValues,
						};
					}
					return null;
				})
				.filter(it => it);

			moveMultipleToBin(params, selectedItems).then(() => {
				getSearchResults(params).then(result => {
					setIsMoveToBinOpen(false);
					setTransactions(result);
					setSelectedTransactions([]);
				});
			});
		}
	};

	const updateQuickFilterSelection = filter => {
		if (quickFilter === filter) {
			setQuickFilter(0);
		} else {
			onSearchClear();
			setQuickFilter(filter);
		}
	};

	const tags = getTags(props);
	const columns = getCols(transactions, selectedDimension);
	const firstColumn = columns && columns.length ? columns.shift() : null;

	return (
		<Fragment>
			<ApButtonIcon
				iconName="search"
				onClick={() => {
					setIsDialogOpen(true);
				}}
			/>
			<Wrapper onClick={e => e.stopPropagation()}>
				<StyledModal
					isOpen={isDialogOpen}
					header={
						<FiltersContainer>
							<FiltersLabel>Search by:</FiltersLabel>
							<ChipsContainer>
								{tags.map((tag, i) => (
									<FilterChip key={i} label={tag} disabled />
								))}
							</ChipsContainer>
						</FiltersContainer>
					}
					isDismissible
					hasClosed={() => {
						setIsDialogOpen(false);
						setSelectedTransactions([]);
						setQuickFilter();
						onSearchClear();
						setSorting({
							columnIndex: 1,
							direction: 'ASC',
						});
					}}
					footer={() => {
						return <div />;
					}}
				>
					<Container>
						<Selection>
							<DimensionDropdown
								options={dimensions.map(dimension => ({
									...dimension,
									label: dimension.displayName.replaceAll('_', ' '),
									value: dimension.displayName,
								}))}
								selected={selectedDimension || { label: 'none' }}
								onChange={option => {
									setLocalStorageItem(
										SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY,
										getTop3Selected([...prevSelectedDimensions], option)
									);
									setSelectedDimension(option);
								}}
								prevSelectedDimensions={prevSelectedDimensions}
							/>
							<DialogSearchWrapper>
								<StyledSearch
									onChange={onSearchChangeDebounced}
									onClear={onSearchClear}
									value={search}
								/>
							</DialogSearchWrapper>
						</Selection>
						<QuickSearchContainer>
							<QuickSearchLabel>Quick search options:</QuickSearchLabel>
							<QuickSearchOptions>
								<FilterChipGroup>
									<FilterChip
										isSelected={quickFilter === 'Empty / Null'}
										label="Empty / Null"
										onClick={() => updateQuickFilterSelection('Empty / Null')}
									/>
									<FilterChip
										isSelected={quickFilter === 'Contains numerical'}
										label="Contains numerical"
										onClick={() =>
											updateQuickFilterSelection('Contains numerical')
										}
									/>
									<FilterChip
										isSelected={quickFilter === 'Numerical only'}
										label="Numerical only"
										onClick={() => updateQuickFilterSelection('Numerical only')}
									/>
									<FilterChip
										isSelected={quickFilter === 'Special characters'}
										label="Special characters"
										onClick={() =>
											updateQuickFilterSelection('Special characters')
										}
									/>
								</FilterChipGroup>
							</QuickSearchOptions>
						</QuickSearchContainer>
						{(transactions && transactions.totalCount) ||
						transactions.totalCount === 0 ? (
							<Fragment>
								<TableHeader>
									<TableHeaderCol width={64}>
										<ApCheckbox
											onChange={() => {
												if (isAllSelectedTransactions) {
													setSelectedTransactions([]);
												} else {
													setSelectedTransactions(
														transactions.body.map(row => row.value)
													);
												}
												setIsAllSelectedTranscations(
													!isAllSelectedTransactions
												);
											}}
											checked={
												transactions.body.length &&
												isEqual(
													transactions.body.map(row => row.value),
													selectedTransactions
												)
											}
										/>
									</TableHeaderCol>
									{selectedTransactions.length ? (
										<Fragment>
											<TableHeaderCol
												align="left"
												width={500}
												noMaxWidth
												onClick={() =>
													setSorting({
														columnIndex: 1,
														direction:
															sorting.direction === 'ASC' ? 'DESC' : 'ASC',
													})
												}
											>
												<span>
													{selectedTransactions.length} items selected ({
														transactions.totalCount
													}{' '}
													transactions)
												</span>
											</TableHeaderCol>
											<DeleteButton onClick={() => setIsMoveToBinOpen(true)}>
												Move {selectedTransactions.length} items to bin
											</DeleteButton>
										</Fragment>
									) : (
										<Fragment>
											<TableHeaderCol
												align={firstColumn.align}
												width={firstColumn.width}
												onClick={() =>
													setSorting({
														columnIndex: firstColumn.sortIndex,
														direction:
															sorting.direction === 'ASC' ? 'DESC' : 'ASC',
													})
												}
											>
												<span>{firstColumn.title}</span>
												{firstColumn.sortIndex === sorting.columnIndex && (
													<ApIcon
														iconName={
															sorting.direction === 'ASC'
																? 'arrow_upward'
																: 'arrow_downward'
														}
														iconColor={colors.grey4}
														iconSize={16}
													/>
												)}
											</TableHeaderCol>
											{columns.map(column => (
												<TableHeaderCol
													key={`header-${column.colName}`}
													align={column.align}
													width={column.width}
													onClick={() =>
														setSorting({
															columnIndex: column.sortIndex,
															direction:
																sorting.direction === 'ASC' ? 'DESC' : 'ASC',
														})
													}
												>
													<span>{column.title}</span>
													{column.sortIndex === sorting.columnIndex && (
														<ApIcon
															iconName={
																sorting.direction === 'ASC'
																	? 'arrow_upward'
																	: 'arrow_downward'
															}
															iconColor={colors.grey4}
															iconSize={16}
														/>
													)}
												</TableHeaderCol>
											))}
											<TableHeaderCol
												width={transactions.body.length > 9 ? 64 : 48}
											/>
										</Fragment>
									)}
								</TableHeader>
								<TableRows>
									{transactions.body.map(row => (
										<TableRow
											key={`row-${row.value}`}
											isSelected={
												isAllSelectedTransactions ||
												selectedTransactions.includes(row.value)
											}
										>
											<TableCell width={64}>
												<ApCheckbox
													onChange={() => {
														if (selectedTransactions.includes(row.value)) {
															const newSelected = [...selectedTransactions];
															newSelected.splice(
																selectedTransactions.indexOf(row.value),
																1
															);
															setSelectedTransactions(newSelected);
															setIsAllSelectedTranscations(false);
														} else {
															const newSelected = [
																...selectedTransactions,
																row.value,
															];
															setSelectedTransactions(newSelected);
															if (
																newSelected.length === transactions.totalCount
															) {
																setIsAllSelectedTranscations(true);
															}
														}
													}}
													checked={selectedTransactions.includes(row.value)}
												/>
											</TableCell>
											{firstColumn ? (
												<TableEllipsisCell
													key={`cell-${firstColumn.colName}-${row.value}`}
													align={firstColumn.align}
													width={firstColumn.width}
												>
													{getColumnValue({
														column: firstColumn,
														currencySymbol,
														row,
														search,
													})}
												</TableEllipsisCell>
											) : null}
											{columns.map(column => (
												<TableCell
													key={`cell-${column.colName}-${row.value}`}
													align={column.align}
													width={column.width}
												>
													{getColumnValue({
														column,
														currencySymbol,
														row,
														search,
													})}
												</TableCell>
											))}
											<TableCell align="left" width={48}>
												<NavigateLink
													iconName="trending_flat"
													onClick={() => {
														if (isPmi) {
															showPMIExplorePage(
																{
																	...convertedPropsFromQueryString,
																	...props,
																	history,
																	selectedDimensionKey:
																		selectedDimension.columnName,
																},
																row.value
															);
														} else {
															setIsDialogOpen(false);
															showAnalysisExplorePage(
																{
																	...props,
																	history,
																	selectedDimensionKey:
																		selectedDimension.columnName,
																},
																row.value
															);
														}
													}}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableRows>

								{transactions.totalCount > 25 && (
									<Pagination>
										<PaginationText>
											{itemsOnPage}/{transactions.totalCount}
										</PaginationText>
										{itemsOnPage < transactions.totalCount && (
											<ApButtonLink
												onClick={() => {
													addItemsOnPage(
														Math.min(itemsOnPage + 10, transactions.totalCount)
													);
												}}
											>
												show more
											</ApButtonLink>
										)}
									</Pagination>
								)}
							</Fragment>
						) : (
							<ApLoaderDefault loaderText={() => <ApLoaderText />} />
						)}
					</Container>
				</StyledModal>
				<MoveToBinViewModal
					isOpen={isMoveToBinOpen}
					onClose={() => setIsMoveToBinOpen(false)}
					moveToBin={moveToBin}
				/>
			</Wrapper>
		</Fragment>
	);
};

export default props => (
	<ApiDataContext.Consumer>
		{({ clearApiCache }) => (
			<SpendCubeSettingsContext.Consumer>
				{({ settings }) => (
					<SettingsOptionsContext.Consumer>
						{({ options }) => (
							<SettingsContext.Consumer>
								{({ companiesList, customFilters: customFilterOptions }) => (
									<QueryStringContext.Consumer>
										{({
											currency,
											currencyType,
											customFilters,
											customFiltersValues,
											waterfallFilter,
											dateFrom,
											dateTo,
											measures,
											showNonSourceable,
											showHarmonizedVendors,
											dimensions,
											showNegativeSpend,
											storedQueryId,
											storedQuery,
											isTailSpend,
											unit,
											showAllCategories,
											showAllSegments,
											waterfallShowAdjusted,
											waterfallShowL4Level,
											setQueryStringStateAndPush,
											tailSpend,
											comparisonChartDimension,
											chartType,
											segmentTabIndex,
											spendByCategoryDimention1,
											spendByCategoryDimention2,
										}) => {
											const {
												waterfallCategory,
												waterfallCategoryLevel,
												waterfallCategoryHierarchy,
											} = convertMeasuresToWaterfallFilter(measures);

											const {
												othersThreshold,
												currency: currencyList,
											} = options;

											isTailSpend = !!isTailSpend && isTailSpend !== 'false';
											showAllSegments =
												!!showAllSegments && showAllSegments !== 'false';

											// fix for discepancy in the query params
											if (!isTailSpend) {
												showAllSegments = false;
											}
											return (
												<SearchDialog
													companiesList={companiesList}
													currency={currency}
													currencySymbol={getCurrencySymbolFromOptions(
														currencyList,
														currency
													)}
													currencyType={currencyType}
													dateFrom={dateFrom}
													dimensions={dimensions}
													dateTo={dateTo}
													customFilters={convertToArray(customFilters)}
													customFiltersValues={convertToArray(
														customFiltersValues
													)}
													waterfallFilter={waterfallFilter}
													customFilterOptions={customFilterOptions}
													hierarchy={measures}
													clearApiCache={clearApiCache}
													includeCustomCategories={isCustomCategory(dimensions)}
													waterfallCategory={waterfallCategory}
													waterfallCategoryLevel={waterfallCategoryLevel}
													waterfallCategoryHierarchy={
														waterfallCategoryHierarchy
													}
													showNonSourceable={showNonSourceable}
													waterfallShowClientCategory={isCustomCategory(
														dimensions
													)}
													showAllCategories={
														!!showAllCategories && showAllCategories !== 'false'
													}
													waterfallShowAdjusted={
														!!waterfallShowAdjusted &&
														waterfallShowAdjusted !== 'false'
													}
													waterfallShowL4Level={
														!!waterfallShowL4Level &&
														waterfallShowL4Level !== 'false'
													}
													// one for PMI, the other for Analysis
													isHarmonized={showHarmonizedVendors === 'true'}
													showHarmonizedVendors={
														!!showHarmonizedVendors &&
														showHarmonizedVendors !== 'false'
													}
													scaleSymbol={unit || ''}
													scaleTier={UNITS_MAP[unit] || UNITS_MAP.m}
													showNegativeSpend={
														!!showNegativeSpend && showNegativeSpend !== 'false'
													}
													storedQueryId={storedQueryId}
													storedQuery={storedQuery}
													// Added to set hierarchy and other props manually
													isTailSpend={isTailSpend === 'true'}
													useNetSpend={
														settings && settings.tailSpend
															? settings.tailSpend.useNetSpend
															: true
													}
													unit={unit}
													showAllSegments={showAllSegments}
													setQueryStringStateAndPush={
														setQueryStringStateAndPush
													}
													tailSpend={tailSpend}
													comparisonChartDimension={comparisonChartDimension}
													chartType={chartType}
													segmentTabIndex={
														segmentTabIndex ? +segmentTabIndex : 0
													}
													spendByCategoryDimention1={
														spendByCategoryDimention1 ||
														'PURCHASING_LEGAL_ENTITY'
													}
													spendByCategoryDimention2={
														spendByCategoryDimention2 ||
														'HARMONIZED_VENDOR_NAME'
													}
													othersThreshold={othersThreshold}
													{...props}
												/>
											);
										}}
									</QueryStringContext.Consumer>
								)}
							</SettingsContext.Consumer>
						)}
					</SettingsOptionsContext.Consumer>
				)}
			</SpendCubeSettingsContext.Consumer>
		)}
	</ApiDataContext.Consumer>
);
